import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { EmailSettings, HealthStatusSettings } from '../../../shared/models/generated/ziroModels';
import { Component, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { EmailSettingsContext } from '../../../shared/contexts/email-settings.context';
import { ZiroMultiCheckboxConfig } from '../../../forms/fields/multi-checkbox/ziro-multi-checkbox.component';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { TranslateService } from '@ngx-translate/core';
import { SystemHealthStatusSettingsResource } from '../../resources/system-health-status-settings.resource';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { cloneDeep, isEqual } from 'lodash';
import { ZiroMultiTextConfig } from '../../../forms/fields/multi-text/ziro-multi-text.component';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ToastService } from '../../../shared/services/toast.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { tap } from 'rxjs/operators';
import {
  HtmlCheckboxType,
  HtmlSwitchSize,
  ZiroCheckboxConfig,
} from '../../../forms/fields/checkbox/ziro-checkbox.component';
import { BreadcrumbsComponent } from '../../../shared/breadcrumbs/breadcrumbs.component';

interface HealthSettingsNotificationsSettingsFormData {
  automaticDisableOfEndpoints: boolean;
  healthCheckIntervalInMinutes: number;
  notificationsTrigger: {
    healthStatusWarningDetected: boolean;
    healthStatusErrorDetected: boolean;
  };
  failureThresholdForWarnings: number;
  repeatNotificationWarnings: number;
  failureThresholdForErrors: number;
  repeatNotificationErrors: number;
  to: string[];
}

@Component({
  selector: 'ziro-admin-system-health-settings-notifications',
  templateUrl: './system-health-settings-notifications.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [SystemHealthStatusSettingsResource],
  standalone: false,
})
export class SystemHealthSettingsNotificationsComponent
  extends ZiroFormAbstractDirective<HealthSettingsNotificationsSettingsFormData>
  implements OnInit, OnDestroy
{
  private bottomNavService = inject(BottomNavService);
  private breadcrumbsService = inject(BreadcrumbsService);
  private emailSettingsContext = inject(EmailSettingsContext);
  private translateService = inject(TranslateService);
  private systemHealthStatusNotificationsResource = inject(SystemHealthStatusSettingsResource);
  protected _ziroFormStateService: ZiroFormStateService;
  private toastService = inject(ToastService);

  @ViewChild(BreadcrumbsComponent) breadcrumbsComponent: BreadcrumbsComponent;

  ziroIcons = ZiroIcons;
  isLoading = true;
  formConfig: ZiroFormConfig;

  private _emailSettings: EmailSettings;
  private _subscriptions = new Subscription();
  private _validators = {
    toEmailValidator: (values: string[]): ZiroFormsValidationState => {
      return values.every((value: string) => {
        return (
          !value ||
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            value,
          )
        );
      })
        ? ZiroFormsValidationState.VALID
        : ZiroFormsValidationState.INVALID;
    },
    minValueValidator: (value: number, isEnabled: boolean, minValue: number): ZiroFormsValidationState => {
      return isEnabled && !isNaN(value) && Number(value) < minValue
        ? ZiroFormsValidationState.INVALID
        : ZiroFormsValidationState.VALID;
    },
  };

  @HostListener('document:click', ['$event']) onAboutCardLinkClick($event: any): void {
    if (!$event.target.matches('.breadcrumb-scroll-to-expand')) {
      return;
    }
    $event.preventDefault();
    this.breadcrumbsComponent.scrollToAndExpand();
  }

  ngOnInit(): void {
    this._initFormConfig();
    this._initBottomNav();
    this._initBreadcrumbs();

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() =>
      this._setBottomNavValidationError(!this.isFormValid()),
    );
    this._subscriptions.add(formSubmittedSub);

    const contextSub = combineLatest([
      this.emailSettingsContext.state$,
      this.systemHealthStatusNotificationsResource.get(),
    ]).subscribe(([data, healthStatusNotification]) => {
      this._emailSettings = data;
      this._setBottomNavDisabled(!this._emailSettings.host || !this._emailSettings.from);
      this.entitySource.next(this._entityToFormData(healthStatusNotification));
      this.isLoading = false;
    });
    this._subscriptions.add(contextSub);

    const updateSub = this.ziroFormsUpdate$.subscribe(
      (update: ZiroFormsUpdate<HealthSettingsNotificationsSettingsFormData>) => {
        if (
          (update.new.notificationsTrigger.healthStatusWarningDetected === true &&
            update.old?.notificationsTrigger.healthStatusWarningDetected === false) ||
          (update.new.notificationsTrigger.healthStatusErrorDetected === true &&
            update.old?.notificationsTrigger.healthStatusErrorDetected === false)
        ) {
          let entityCopy = cloneDeep(update.new);

          if (!update.new.failureThresholdForWarnings) {
            entityCopy = {
              ...entityCopy,
              failureThresholdForWarnings: 3,
            };
          }

          if (update.new.repeatNotificationWarnings === null) {
            entityCopy = {
              ...entityCopy,
              repeatNotificationWarnings: 30,
            };
          }

          if (!update.new.failureThresholdForErrors) {
            entityCopy = {
              ...entityCopy,
              failureThresholdForErrors: 3,
            };
          }

          if (update.new.repeatNotificationErrors === null) {
            entityCopy = {
              ...entityCopy,
              repeatNotificationErrors: 30,
            };
          }

          if (!isEqual(update.new, entityCopy)) {
            this.entitySource.next(entityCopy);
          }
        }
      },
    );
    this._subscriptions.add(updateSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.breadcrumbsService.clearBreadcrumbs();
    this._subscriptions.unsubscribe();
  }

  hasEmailSettings(): boolean {
    return !!this._emailSettings?.host;
  }

  protected submit(): Observable<void> {
    return this._onFormSubmit();
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        automaticDisableOfEndpoints: {
          label: 'tkey;admin.system_health_status_notifications.automatic_disable_of_endpoints.label',
          dataAutomation: 'system-health-settings-notifications-automatic-disable-of-endpoints',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
          options: {
            content: 'tkey;admin.system_health_status_notifications.automatic_disable_of_endpoints.helptext',
          },
        },
        healthCheckIntervalInMinutes: {
          label: 'tkey;admin.system_health_status_notifications.health_check_interval_in_minutes.label',
          dataAutomation: 'system-health-settings-notifications-health-check-interval-in-minutes',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
          required: () => true,
          validation: [
            {
              validator: (val: number) => {
                return val >= 3 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
              },
              message: 'tkey;admin.system_health_status_notifications.health_check_interval_in_minutes.validation',
            },
          ],
          options: {
            content: 'tkey;admin.system_health_status_notifications.health_check_interval_in_minutes.helptext',
          },
        },
        notificationsTrigger: {
          label: 'tkey;admin.system_health_status_notifications.notifications_triggers.label',
          dataAutomation: 'system-health-settings-notifications-notifications-triggers',
          required: false,
          componentConfig: new ZiroMultiCheckboxConfig({
            multiCheckboxOptionConfig: [
              {
                label: 'tkey;admin.system_health_status_notifications.health_status_warning_detected.label',
                hidden: () => false,
                disabled: () => false,
              },
              {
                label: 'tkey;admin.system_health_status_notifications.health_status_error_detected.label',
                hidden: () => false,
                disabled: () => false,
              },
            ],
          }),
        },
        failureThresholdForWarnings: {
          label: 'tkey;admin.system_health_status_notifications.failure_threshold_warnings.label',
          dataAutomation: 'system-health-settings-notifications-failure-threshold-warnings',
          required: () => {
            return this.formData?.notificationsTrigger?.healthStatusWarningDetected;
          },
          hidden: () => {
            return !this.formData?.notificationsTrigger?.healthStatusWarningDetected;
          },
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            min: 1,
          }),
          helpText: () =>
            this.translateService.instant(
              'tkey;admin.system_health_status_notifications.failure_threshold_warnings.helptext',
              {
                value: this.formData.failureThresholdForWarnings * 3,
              },
            ),
          validation: [
            {
              validator: (value: number) => {
                return this._validators.minValueValidator(
                  value,
                  this.formData?.notificationsTrigger?.healthStatusWarningDetected,
                  1,
                );
              },
              message: () =>
                this.translateService.instant('tkey;validators.global.error.min', {
                  minValue: 1,
                }),
            },
          ],
        },
        repeatNotificationWarnings: {
          label: 'tkey;admin.system_health_status_notifications.repeat_notifications_warnings.label',
          dataAutomation: 'system-health-settings-notifications-repeat-notifications-warnings',
          required: () => {
            return this.formData?.notificationsTrigger?.healthStatusWarningDetected;
          },
          hidden: () => {
            return !this.formData?.notificationsTrigger?.healthStatusWarningDetected;
          },
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            min: 0,
          }),
          helpText: () =>
            this.translateService.instant(
              'tkey;admin.system_health_status_notifications.repeat_notifications_warnings.helptext',
              {
                value: this.formData.repeatNotificationWarnings,
              },
            ),
          validation: [
            {
              validator: (value: number) => {
                return this._validators.minValueValidator(
                  value,
                  this.formData?.notificationsTrigger?.healthStatusWarningDetected,
                  0,
                );
              },
              message: () =>
                this.translateService.instant('tkey;validators.global.error.min', {
                  minValue: 0,
                }),
            },
          ],
        },
        failureThresholdForErrors: {
          label: 'tkey;admin.system_health_status_notifications.features_threshold_errors.label',
          dataAutomation: 'system-health-settings-notifications-failure-threshold-errors',
          required: () => {
            return this.formData?.notificationsTrigger?.healthStatusErrorDetected;
          },
          hidden: () => {
            return !this.formData?.notificationsTrigger?.healthStatusErrorDetected;
          },
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            min: 1,
          }),
          helpText: () =>
            this.translateService.instant(
              'tkey;admin.system_health_status_notifications.features_threshold_errors.helptext',
              {
                value: this.formData.failureThresholdForErrors * 3,
              },
            ),
          validation: [
            {
              validator: (value: number) => {
                return this._validators.minValueValidator(
                  value,
                  this.formData?.notificationsTrigger?.healthStatusErrorDetected,
                  1,
                );
              },
              message: () =>
                this.translateService.instant('tkey;validators.global.error.min', {
                  minValue: 1,
                }),
            },
          ],
        },
        repeatNotificationErrors: {
          label: 'tkey;admin.system_health_status_notifications.repeat_notifications_errors.label',
          dataAutomation: 'system-health-settings-notifications-repeat-notifications-errors',
          required: () => {
            return this.formData?.notificationsTrigger?.healthStatusErrorDetected;
          },
          hidden: () => {
            return !this.formData?.notificationsTrigger?.healthStatusErrorDetected;
          },
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            min: 0,
          }),
          helpText: () =>
            this.translateService.instant(
              'tkey;admin.system_health_status_notifications.repeat_notifications_errors.helptext',
              {
                value: this.formData.repeatNotificationErrors,
              },
            ),
          validation: [
            {
              validator: (value: number) => {
                return this._validators.minValueValidator(
                  value,
                  this.formData?.notificationsTrigger?.healthStatusErrorDetected,
                  0,
                );
              },
              message: () =>
                this.translateService.instant('tkey;validators.global.error.min', {
                  minValue: 0,
                }),
            },
          ],
        },
        to: {
          label: 'tkey;admin.system_health_status_notifications.to.label',
          dataAutomation: 'system-health-settings-notifications-to',
          required: () => {
            return (
              this.formData?.notificationsTrigger?.healthStatusWarningDetected ||
              this.formData?.notificationsTrigger?.healthStatusErrorDetected
            );
          },
          componentConfig: new ZiroMultiTextConfig({ placeholder: 'ziroadministrators@yourcompany.com' }),
          helpText: () => 'tkey;admin.system_health_status_notifications.to.helptext',
          validation: [
            {
              validator: this._validators.toEmailValidator,
              message: 'tkey;validators.global.email.invalid.error',
            },
          ],
        },
      },
    };

    this.entitySource.next({
      automaticDisableOfEndpoints: false,
      notificationsTrigger: {
        healthStatusWarningDetected: false,
        healthStatusErrorDetected: false,
      },
      healthCheckIntervalInMinutes: 15,
      failureThresholdForWarnings: null,
      repeatNotificationWarnings: null,
      failureThresholdForErrors: null,
      repeatNotificationErrors: null,
      to: [],
    });
  }

  private _onFormSubmit(): Observable<void> {
    this._setBottomNavPending(true);
    this._setBottomNavValidationError(false);

    return this.systemHealthStatusNotificationsResource.put(this._formDataToEntity()).pipe(
      tap(() => {
        this._setBottomNavPending(false);

        this.toastService.push(
          ToastTypes.SUCCESS,
          this.ziroIcons.NOTIFICATION,
          'tkey;shared.toast.save.success.title',
          'tkey;admin.system_health_status_notifications.toast.success',
        );
      }),
    );
  }

  private _entityToFormData(entity: HealthStatusSettings): HealthSettingsNotificationsSettingsFormData {
    return {
      automaticDisableOfEndpoints: entity.automaticDisableOfEndpoints,
      notificationsTrigger: {
        healthStatusWarningDetected: !!(
          entity.warningNotifications?.failureThreshold || entity.warningNotifications?.repeatNotificationInMinutes
        ),
        healthStatusErrorDetected: !!(
          entity.errorNotifications?.failureThreshold || entity.errorNotifications?.repeatNotificationInMinutes
        ),
      },
      healthCheckIntervalInMinutes: entity.healthCheckIntervalInMinutes,
      failureThresholdForWarnings: entity.warningNotifications?.failureThreshold,
      repeatNotificationWarnings: entity.warningNotifications?.repeatNotificationInMinutes,
      failureThresholdForErrors: entity.errorNotifications?.failureThreshold,
      repeatNotificationErrors: entity.errorNotifications?.repeatNotificationInMinutes,
      to: entity.emailRecipients && entity.emailRecipients[0] ? entity.emailRecipients : [],
    };
  }

  private _formDataToEntity(): HealthStatusSettings {
    return {
      automaticDisableOfEndpoints: this.formData.automaticDisableOfEndpoints,
      emailRecipients: this.formData.to,
      errorNotifications: this.formData.notificationsTrigger.healthStatusErrorDetected
        ? {
            failureThreshold: this.formData.failureThresholdForErrors,
            repeatNotificationInMinutes: this.formData.repeatNotificationErrors,
          }
        : null,
      healthCheckIntervalInMinutes: this.formData.healthCheckIntervalInMinutes,
      warningNotifications: this.formData.notificationsTrigger.healthStatusWarningDetected
        ? {
            failureThreshold: this.formData.failureThresholdForWarnings,
            repeatNotificationInMinutes: this.formData.repeatNotificationWarnings,
          }
        : null,
    };
  }

  private _setBottomNavValidationError(hasError: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: hasError,
      }),
    );
  }

  private _setBottomNavPending(setting: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'system-health-settings-notifications-save-button',
        state: {
          pending: setting,
          buttonDisableState: {
            disabled: setting,
            tooltipKey: '',
          },
        },
      }),
    );
  }

  private _setBottomNavDisabled(setting: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'system-health-settings-notifications-save-button',
        state: {
          pending: false,
          buttonDisableState: {
            disabled: setting,
            tooltipKey: '',
          },
        },
      }),
    );
  }

  private _initBottomNav(): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'system-health-settings-notifications-save-button',
          label: 'tkey;admin.ui.save',
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'system-health-settings-notifications-save-button',
          state: {
            pending: false,
            buttonDisableState: {
              disabled: false,
              tooltipKey: '',
            },
          },
          icon: this.ziroIcons.SAVE_SUCCESS,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );
  }

  private _initBreadcrumbs(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.system_health_status_notifications.title' }]);
  }
}
